<template>
  <div class="bg-white onboarding-form-builder">
    <!-- Header -->
    <div class="sticky-header">
      <h1 class="text-lg font-semibold p-4 mb-0">{{ formData.title }} v{{ formData.version }}</h1>
      <div class="p-4">
        <v-icon @click="$emit('close')" class="cursor-pointer">mdi-close</v-icon>
      </div>
    </div>

    <!-- Scrollable content -->
    <div class="scrollable-content">
      <v-row>
        <v-col
          v-for="section in formData.sections"
          :key="section.id"
          cols="12"
          sm="6"
          md="6"
          lg="6"
          xl="6"
        >
          <v-card class="section-card" elevation="0" max-height="500">
            <h1 class="section-title text-lg p-2">
              {{ section.title }}
            </h1>

            <v-card-text>
              <div v-for="field in section.fields" :key="field.key" class="field-item">
                <v-checkbox
                  v-model="selectedFields"
                  :label="field.label"
                  :value="`${section.id}.${field.key}`"
                  :hint="field.tooltip"
                  persistent-hint
                  dense
                  :indeterminate="isParentPartiallySelected(section, field)"
                  :disabled="field.required"
                  @change="handleParentCheckboxChange(section, field, $event)"
                >
                  <template v-slot:append>
                    <v-chip small outlined color="red" label v-if="field.required">
                      {{ field.required ? 'Required' : '' }}
                    </v-chip>
                    <v-chip small outlined color="purple" label class="ml-2" v-if="field.audience">
                      {{ field.audience === 'BOTH' ? 'User / Office' : field.audience }}
                    </v-chip>
                    <v-chip
                      small
                      outlined
                      label
                      :color="getFieldTypeColor(field.type)"
                      class="ml-2"
                    >
                      {{ titleCase(field.type) }}
                    </v-chip>
                  </template>
                </v-checkbox>

                <!-- Handle nested fields for object type -->
                <div v-if="field.type === 'object' && field.fields" class="nested-fields ml-8">
                  <div
                    v-for="nestedField in field.fields"
                    :key="nestedField.key"
                    class="nested-field-item"
                  >
                    <v-checkbox
                      v-model="selectedFields"
                      :label="nestedField.label"
                      :value="`${section.id}.${field.key}.${nestedField.key}`"
                      :hint="nestedField.tooltip"
                      persistent-hint
                      dense
                      :disabled="nestedField.required"
                      @change="handleChildCheckboxChange(section, field, nestedField, $event)"
                    >
                      <template v-slot:append>
                        <v-chip
                          small
                          outlined
                          label
                          :color="getFieldTypeColor(nestedField.type)"
                          class="ml-2"
                        >
                          {{ nestedField.type }}
                        </v-chip>
                      </template>
                    </v-checkbox>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-divider class="mt-4" v-if="selectedFields.length > 0"></v-divider>
    </div>

    <!-- Sticky Save Button -->
    <div class="sticky-footer">
      <v-card flat class="pa-4 mb-0">
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveSelectedFields" small>
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
import { convertToTitleCase } from '@/utils/common'

export default {
  name: 'OnboardingFormBuilder',
  data() {
    return {
      formData: {},
      selectedFields: []
    }
  },
  props: {
    selectedPredefinedSection: String,
    existingQuestions: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    selectedPredefinedSection: {
      handler(newVal) {
        if (newVal) {
          this.getPrefinedData()
        }
      },
      immediate: true
    },
    // Watch for existing questions and update selected fields
    existingQuestions: {
      handler(newVal) {
        if (newVal) {
          this.updateSelectedFieldsFromExisting(newVal)
        }
      },
      immediate: true
    }
  },
  methods: {
    titleCase(str) {
      return convertToTitleCase(str)
    },
    async getPrefinedData() {
      try {
        const params = {
          category: this.selectedPredefinedSection
        }
        const response = await this.$axios.get(`/orgSetup/predefined-special-category`, { params })
        const data = response.data?.data?.[0]
        this.formData = data

        // Update selected fields based on existing questions
        this.updateSelectedFieldsFromExisting(this.existingQuestions)
      } catch (error) {
        console.log(error)
      }
    },
    // New method to update selected fields based on existing questions
    updateSelectedFieldsFromExisting(existingQuestions) {
      // Reset selected fields
      this.selectedFields = []

      // Auto select required fields
      this.autoSelectRequiredFields()

      if (!existingQuestions || existingQuestions.length === 0 || !this.formData.sections) {
        return
      }

      // Convert existing questions structure to selected field paths
      // Only process section-based questions (from special category), not regular questions
      existingQuestions.forEach((questionItem) => {
        if (questionItem.sectionName && questionItem.subQuestions) {
          // Handle section with subQuestions (special category questions)
          questionItem.subQuestions.forEach((subQuestion) => {
            // Check if this is a nested section (section within a section)
            if (subQuestion.sectionName && subQuestion.subQuestions) {
              // This is a nested section, process its subQuestions
              subQuestion.subQuestions.forEach((nestedSubQuestion) => {
                this.formData.sections.forEach((section) => {
                  section.fields.forEach((field) => {
                    if (field.fields) {
                      field.fields.forEach((nestedField) => {
                        if (
                          nestedField.label === nestedSubQuestion.question &&
                          field.label === subQuestion.sectionName
                        ) {
                          const val = `${section.id}.${field.key}.${nestedField.key}`
                          if (!this.selectedFields.includes(val)) {
                            this.selectedFields.push(val)
                          }
                        }
                      })
                    }
                  })
                })
              })
            } else {
              // Regular subQuestion (not a nested section)
              this.formData.sections.forEach((section) => {
                section.fields.forEach((field) => {
                  if (field.label === subQuestion.question && !field.fields) {
                    const val = `${section.id}.${field.key}`
                    if (!this.selectedFields.includes(val)) {
                      this.selectedFields.push(val)
                    }
                  }
                  // Handle nested fields
                  if (field.fields) {
                    field.fields.forEach((nestedField) => {
                      if (nestedField.label === subQuestion.question) {
                        // Nested field
                        const val = `${section.id}.${field.key}.${nestedField.key}`
                        if (!this.selectedFields.includes(val)) {
                          this.selectedFields.push(val)
                        }
                      }
                    })
                  }
                })
              })
            }
          })
        }
        // Skip regular questions (those without sectionName) as they're not part of the special category
      })
    },
    getFieldTypeColor(type) {
      const colorMap = {
        TEXT: 'blue',
        // email: 'green',
        NUMBER: 'teal',
        // date: 'purple',
        // time: 'indigo',
        SELECT_OPTIONS: 'orange',
        // multiselect: 'deep-orange',
        // RADIO: 'cyan',
        LOCAL_ATTACHMENTS: 'red',
        TEXTAREA: 'pink'
        // url: 'light-blue',
        // object: 'grey'
      }
      return colorMap[type] || 'grey'
    },
    getFieldLabel(fieldPath) {
      // Parse the field path to get a human-readable label
      const parts = fieldPath.split('.')
      if (parts.length === 2) {
        const [sectionId, fieldKey] = parts
        const section = this.formData.sections.find((s) => s.id === sectionId)
        if (section) {
          const field = section.fields.find((f) => f.key === fieldKey)
          if (field) return `${section.title} > ${field.label}`
        }
      } else if (parts.length === 3) {
        const [sectionId, fieldKey, nestedFieldKey] = parts
        const section = this.formData.sections.find((s) => s.id === sectionId)
        if (section) {
          const field = section.fields.find((f) => f.key === fieldKey)
          if (field && field.fields) {
            const nestedField = field.fields.find((f) => f.key === nestedFieldKey)
            if (nestedField) return `${section.title} > ${field.label} > ${nestedField.label}`
          }
        }
      }
      return fieldPath
    },
    removeField(fieldPath) {
      const index = this.selectedFields.indexOf(fieldPath)
      if (index > -1) {
        this.selectedFields.splice(index, 1)
      }
    },
    // Check if a parent checkbox should be in indeterminate state
    isParentPartiallySelected(section, field) {
      // Only applies to object type fields with nested fields
      if (field.type !== 'object' || !field.fields || field.fields.length === 0) {
        return false
      }

      // Count how many child fields are selected
      let selectedCount = 0
      field.fields.forEach((nestedField) => {
        const fieldValue = `${section.id}.${field.key}.${nestedField.key}`
        if (this.selectedFields.includes(fieldValue)) {
          selectedCount++
        }
      })

      // Return true if some but not all children are selected
      return selectedCount > 0 && selectedCount < field.fields.length
    },

    // Handle parent checkbox change (select/deselect all children)
    handleParentCheckboxChange(section, field, isChecked) {
      // Only applies to object type fields with nested fields
      if (field.type !== 'object' || !field.fields || field.fields.length === 0) {
        return
      }

      if (isChecked) {
        // If parent is checked, add all children
        field.fields.forEach((nestedField) => {
          const childValue = `${section.id}.${field.key}.${nestedField.key}`
          if (!this.selectedFields.includes(childValue)) {
            this.selectedFields.push(childValue)
          }
        })
      } else {
        // If parent is unchecked, remove all children except required ones
        field.fields.forEach((nestedField) => {
          if (nestedField.required) return

          const childValue = `${section.id}.${field.key}.${nestedField.key}`
          const index = this.selectedFields.indexOf(childValue)
          if (index > -1) {
            this.selectedFields.splice(index, 1)
          }
        })
      }
    },

    autoSelectRequiredFields() {
      if (!this.formData.sections) return

      this.formData.sections.forEach((section) => {
        section.fields.forEach((field) => {
          // Handle top-level required fields
          if (field.required && field.type !== 'object') {
            const fieldPath = `${section.id}.${field.key}`
            if (!this.selectedFields.includes(fieldPath)) {
              this.selectedFields.push(fieldPath)
            }
          }

          // Handle nested required fields
          if (field.fields) {
            field.fields.forEach((nestedField) => {
              if (nestedField.required) {
                const nestedPath = `${section.id}.${field.key}.${nestedField.key}`
                if (!this.selectedFields.includes(nestedPath)) {
                  this.selectedFields.push(nestedPath)
                }
              }
            })
          }
        })
      })
    },

    // Handle child checkbox change (update parent state)
    handleChildCheckboxChange(section, field, nestedField, isChecked) {
      // This is handled automatically by Vue's v-model
      // We just need to ensure the parent checkbox updates its indeterminate state
      // which happens automatically through the isParentPartiallySelected method
      // No additional action needed here
    },
    saveSelectedFields() {
      // Create the structure as requested
      const result = this.selectedFields
        .map((fieldPath) => {
          const parts = fieldPath.split('.')

          if (parts.length === 2) {
            // Handle simple fields (sectionId.fieldKey)
            const [sectionId, fieldKey] = parts
            const section = this.formData.sections.find((s) => s.id === sectionId)
            if (section) {
              const field = section.fields.find((f) => f.key === fieldKey)
              // Skip object type fields as they should only act as containers
              if (field && field.type !== 'object') {
                return {
                  sectionName: section.title,
                  subQuestions: [
                    {
                      question: field.label,
                      questionType: field.type !== 'object' ? field.type : undefined,
                      mappedKeyField: field.key,
                      audience: field.audience,
                      fetchUrl: field.fetchUrl || undefined,
                      ref: field.ref || undefined,
                      resKey: field.resKey || undefined,
                      labelKey: field.labelKey || undefined,
                      isRequired: field.required,
                      options: field.options || [],
                      validationMessage: field.required ? field.tooltip || field.label : '',
                      userResponse: null
                    }
                  ]
                }
              }
            }
          } else if (parts.length > 2) {
            // Handle nested fields (any depth)
            const [sectionId, parentFieldKey, ...nestedFieldKeys] = parts
            const section = this.formData.sections.find((s) => s.id === sectionId)
            if (section) {
              const parentField = section.fields.find((f) => f.key === parentFieldKey)
              if (parentField && parentField.fields) {
                // For nested fields, we only want to create the leaf nodes
                // and group them under their parent section
                const leafField = this.findLeafField(parentField, nestedFieldKeys)
                if (leafField) {
                  return {
                    sectionName: section.title,
                    parentField: parentField.label, // Track the parent field name
                    subQuestions: [
                      {
                        question: leafField.label,
                        questionType: leafField.type !== 'object' ? leafField.type : undefined,
                        mappedKeyField: leafField.key,
                        audience: leafField.audience,
                        fetchUrl: leafField.fetchUrl || undefined,
                        ref: leafField.ref || undefined,
                        resKey: leafField.resKey || undefined,
                        labelKey: leafField.labelKey || undefined,
                        isRequired: leafField.required,
                        options: leafField.options || [],
                        validationMessage: leafField.required
                          ? leafField.tooltip || leafField.label
                          : '',
                        userResponse: null
                      }
                    ]
                  }
                }
              }
            }
          }
          return null
        })
        .filter((item) => item !== null)

      // Group by section and parent field
      const groupedResult = this.groupSectionsWithNestedFields(result)

      console.log('Selected Questions Structure:', groupedResult)
      // You can also emit an event or make an API call here
      this.$emit('questions-saved', groupedResult)
    },

    findLeafField(parentField, nestedFieldKeys) {
      let currentField = parentField
      for (const key of nestedFieldKeys) {
        if (currentField && currentField.fields) {
          currentField = currentField.fields.find((f) => f.key === key)
        } else {
          return null
        }
      }
      return currentField
    },

    // Helper method to group sections and nested fields properly
    groupSectionsWithNestedFields(result) {
      const grouped = {}

      result.forEach((item) => {
        if (!grouped[item.sectionName]) {
          grouped[item.sectionName] = {
            sectionName: item.sectionName,
            subQuestions: []
          }
        }

        // If this item has a parent field, we need to group under that
        if (item.parentField) {
          // Check if we already have a section for this parent field
          let parentSection = grouped[item.sectionName].subQuestions.find(
            (q) => q.sectionName === item.parentField
          )

          if (!parentSection) {
            // Create a new section for the parent field
            parentSection = {
              sectionName: item.parentField,
              subQuestions: []
            }
            grouped[item.sectionName].subQuestions.push(parentSection)
          }

          // Add the subQuestions to the parent section
          parentSection.subQuestions.push(...item.subQuestions)
        } else {
          // Regular subQuestion, add directly
          grouped[item.sectionName].subQuestions.push(...item.subQuestions)
        }
      })

      return Object.values(grouped)
    }
  }
}
</script>

<style scoped>
.section-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.section-title {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.field-item {
  border-bottom: 1px solid #eee;
  padding: 8px 0;
}

.field-item:last-child {
  border-bottom: none;
}

.nested-fields {
  border-left: 2px solid #e0e0e0;
  padding-left: 12px;
  margin-top: 8px;
}

.nested-field-item {
  padding: 4px 0;
}

/* Add scrollable container for the entire form builder */
.onboarding-form-builder {
  display: flex;
  flex-direction: column;
  height: 80vh;
  overflow: hidden;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background-color: white;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
}

/* Add this new style for scrollable card content */
.section-card .v-card__text {
  overflow-y: auto;
  max-height: 300px;
}
</style>
